import axios, {
    AxiosHeaders,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";
import { ApiResponse } from "./httpTypes";

function clearFormDataContentType(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    if (!(config.data instanceof FormData) || !config.headers) {
        return config;
    }

    if (config.headers instanceof AxiosHeaders) {
        config.headers.setContentType(false);
        return config;
    }

    delete config.headers["Content-Type"];

    return config;
}

export type UnauthorizedHandler = (error: unknown) => Promise<string | null> | string | null;

type RetryableConfig = InternalAxiosRequestConfig & { _maeizRetried?: boolean };

export class HttpClient {
    private instance: AxiosInstance;
    private unauthorizedHandler: UnauthorizedHandler | null = null;
    private refreshInFlight: Promise<string | null> | null = null;

    constructor(baseURL: string, defaultHeaders?: Record<string, string>) {
        this.instance = axios.create({
            baseURL,
            headers: {
                "Content-Type": "application/json",
                ...defaultHeaders,
            },
        });

        this.instance.interceptors.request.use(clearFormDataContentType);
        this.instance.interceptors.response.use(
            (response) => response,
            (error) => this.handleResponseError(error)
        );
    }

    setAuthToken(token: string | null): void {
        if (token) {
            this.instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } else {
            delete this.instance.defaults.headers.common["Authorization"];
        }
    }

    /**
     * Register a handler invoked once when a request fails with 401 (auth routes
     * excluded). It should obtain a fresh token (returning it) or return null.
     * On a non-null token the failed request is retried once with the new token;
     * concurrent 401s share a single handler invocation.
     */
    setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
        this.unauthorizedHandler = handler;
    }

    private shouldAttemptRefresh(error: unknown): boolean {
        const err = error as { response?: { status?: number }; config?: RetryableConfig };
        if (!this.unauthorizedHandler || err?.response?.status !== 401) {
            return false;
        }
        const config = err.config;
        if (!config || config._maeizRetried) {
            return false;
        }
        return !String(config.url ?? "").includes("/auth/");
    }

    private async handleResponseError(error: unknown): Promise<AxiosResponse> {
        if (!this.shouldAttemptRefresh(error)) {
            return Promise.reject(error);
        }
        const config = (error as { config: RetryableConfig }).config;
        config._maeizRetried = true;

        let refresh = this.refreshInFlight;
        if (!refresh) {
            refresh = Promise.resolve()
                .then(() => this.unauthorizedHandler!(error))
                .catch(() => null);
            this.refreshInFlight = refresh;
            void refresh.finally(() => {
                if (this.refreshInFlight === refresh) {
                    this.refreshInFlight = null;
                }
            });
        }

        const token = await refresh;
        if (!token) {
            return Promise.reject(error);
        }

        this.setAuthToken(token);
        if (config.headers instanceof AxiosHeaders) {
            config.headers.set("Authorization", `Bearer ${token}`);
        } else if (config.headers) {
            (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
        }
        return this.instance.request(config);
    }

    setRequestInterceptor(
        onFulfilled?: (
            config: InternalAxiosRequestConfig
        ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
        onRejected?: (error: unknown) => unknown
    ): void {
        this.instance.interceptors.request.use(onFulfilled, onRejected);
    }

    setResponseInterceptor(
        onFulfilled?: (response: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
        onRejected?: (error: unknown) => unknown
    ): void {
        this.instance.interceptors.response.use(onFulfilled, onRejected);
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        const response = await this.instance.get<ApiResponse<T>>(url, config);
        return response.data;
    }

    async post<T>(
        url: string,
        data?: unknown,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        const response = await this.instance.post<ApiResponse<T>>(url, data, config);
        return response.data;
    }

    async put<T>(
        url: string,
        data?: unknown,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        const response = await this.instance.put<ApiResponse<T>>(url, data, config);
        return response.data;
    }

    async patch<T>(
        url: string,
        data?: unknown,
        config?: AxiosRequestConfig
    ): Promise<ApiResponse<T>> {
        const response = await this.instance.patch<ApiResponse<T>>(url, data, config);
        return response.data;
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        const response = await this.instance.delete<ApiResponse<T>>(url, config);
        return response.data;
    }
}
